import type { ReactNode } from "react";

import { ErrorState, ListEmptyState } from "@/ds";
import { ObrasListaTable, TipoObraTags } from "@/shared/components";
import { FinanceiroCondicao } from "../financeiro-condicao";
import { FINANCEIRO_ERROR_TITLE, FINANCEIRO_NO_VALUE } from "../../financeiro-obras.constants";
import {
  formatDuration,
  formatMillions,
  formatObraDate,
  formatPercent,
} from "../../financeiro-obras.utils";
import type { FinanceiroObrasColumnKey } from "../../types-financeiro-obras";
import { FinanceiroStatusTag } from "../financeiro-status-tag";
import type {
  FinanceiroObraItem,
  FinanceiroObrasListaViewModel,
  FinanceiroObrasSortField,
} from "./types-financeiro-obras-lista-view";

/** Conteúdo da célula; indicadores sem fonte de dados chegam `null` e viram "-". */
function renderCell(
  key: FinanceiroObrasColumnKey,
  obra: FinanceiroObraItem,
  compacto: boolean,
): ReactNode {
  switch (key) {
    case "id":
      // Código maior que a coluna termina em reticências: o nome inteiro fica na dica nativa.
      return <span title={obra.codigo}>{obra.codigo}</span>;
    case "embarcacao":
      return obra.embarcacao.nome || FINANCEIRO_NO_VALUE;
    case "statusFinanceiro":
      return (
        <FinanceiroStatusTag
          status={obra.statusFinanceiro}
          testId={`financeiro-obras-status-${obra.id}`}
        />
      );
    case "tipoObra":
      return (
        <TipoObraTags
          tipos={obra.tiposObra.map((tipo) => ({ codigo: tipo.codigo, tipo: tipo.nome }))}
          compacto={compacto}
        />
      );
    case "realizado":
      return formatMillions(obra.realizadoBrlMilhoes);
    case "outlook":
      return formatMillions(obra.outlookBrlMilhoes);
    case "re":
      return formatPercent(obra.realizadoPercentual);
    case "em":
      return formatPercent(obra.empenhadoPercentual);
    case "co":
      return formatPercent(obra.comprometidoPercentual);
    case "es":
      return formatPercent(obra.estimadoPercentual);
    case "nc":
      return formatPercent(obra.naoComprometidoPercentual);
    case "condicao":
      return (
        <FinanceiroCondicao
          condicao={obra.condicao}
          testId={`financeiro-obras-condicao-${obra.id}`}
        />
      );
    case "inicio":
      return formatObraDate(obra.inicio);
    case "termino":
      return formatObraDate(obra.fim);
    case "duracao":
      return formatDuration(obra.duracaoDias);
    default:
      return null;
  }
}

/**
 * Visão Lista: a tabela compartilhada com Obras (`ObrasListaTable`), paginada, ordenada e filtrada no servidor. ID e
 * Embarcação ficam fixas à esquerda e a lupa de Detalhamento à direita (Figma 12898:247065); no mobile, ID e embarcação
 * formam a coluna Obra, fixa.
 */
export function FinanceiroObrasListaView({
  reload,
  hasSearch,
  hasFilters,
  onClear,
  ...tabela
}: FinanceiroObrasListaViewModel) {
  return (
    <ObrasListaTable<FinanceiroObraItem, FinanceiroObrasColumnKey, FinanceiroObrasSortField>
      {...tabela}
      renderCell={renderCell}
      empty={
        <ListEmptyState
          noun="obra"
          gender="f"
          hasSearch={hasSearch}
          hasFilters={hasFilters}
          onClear={onClear}
          testId="financeiro-obras-empty"
        />
      }
      error={
        <ErrorState
          message={FINANCEIRO_ERROR_TITLE}
          description={tabela.errorMessage ?? undefined}
          onRetry={reload}
          testId="financeiro-obras-error"
        />
      }
    />
  );
}
